/**
 * Formatters globais do TIDE Alpha (locale pt-BR).
 *
 * Regra: TODAS as páginas devem usar estes formatters. Nada de toFixed/toLocaleString
 * inline. Unidades vão no HEADER da coluna, não na célula.
 *
 * Fator K é decimal no JSON (0.085) mas exibido como porcentagem (8,5%).
 *
 * toXlsxCell helper fica pronto aqui para consumo no prompt P-Relatório — ainda não usado.
 */

export const TRAVESSAO = "—";

type Nullish = number | null | undefined;

function isNil(v: Nullish): v is null | undefined {
  return v === null || v === undefined || Number.isNaN(v as number);
}

// ---------------------------------------------------------------------------
// Cached Intl.NumberFormat instances (perf)
// ---------------------------------------------------------------------------
const nfVolumeMil = new Intl.NumberFormat("pt-BR", {
  useGrouping: true,
  maximumFractionDigits: 0,
});
const nfVolumeSmall = new Intl.NumberFormat("pt-BR", {
  useGrouping: false,
  maximumFractionDigits: 0,
});
const nfPercent = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const nfFactor = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});
const nf1Dec = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const nfInt = new Intl.NumberFormat("pt-BR", {
  useGrouping: false,
  maximumFractionDigits: 0,
});

// ---------------------------------------------------------------------------
// Display formatters (UI)
// ---------------------------------------------------------------------------

/** Volume (veh/dia, veh/hora) — inteiro, pt-BR milhar para ≥1000; sem separador <1000. */
export function formatVolume(n: Nullish): string {
  if (isNil(n)) return TRAVESSAO;
  const abs = Math.abs(n as number);
  const rounded = Math.round(n as number);
  const fmt = abs >= 1000 ? nfVolumeMil : nfVolumeSmall;
  return fmt.format(rounded);
}

/** Porcentagem a partir de decimal (0.085 → "8,5%"). */
export function formatPercent(decimal: Nullish): string {
  if (isNil(decimal)) return TRAVESSAO;
  return `${nfPercent.format((decimal as number) * 100)}%`;
}

/** Fator adimensional (FED, FS, FM, FCS, fator_acumulado) — 4 casas decimais. */
export function formatFactor(n: Nullish): string {
  if (isNil(n)) return TRAVESSAO;
  return nfFactor.format(n as number);
}

/** FC pesados — 1 casa decimal. */
export function formatFCPesados(n: Nullish): string {
  if (isNil(n)) return TRAVESSAO;
  return nf1Dec.format(n as number);
}

/** Velocidade — inteiro (km/h no header). */
export function formatVelocidade(n: Nullish): string {
  if (isNil(n)) return TRAVESSAO;
  return nfInt.format(Math.round(n as number));
}

/** Distância / extensão — 1 casa decimal (km no header). */
export function formatDistancia(n: Nullish): string {
  if (isNil(n)) return TRAVESSAO;
  return nf1Dec.format(n as number);
}

/** Quilometragem de posto (ex: 18.5 → "18,5"). 1 casa decimal, vírgula BR. */
export function formatKm(n: Nullish): string {
  if (isNil(n)) return TRAVESSAO;
  return nf1Dec.format(n as number);
}

const decimalFormatters = new Map<number, Intl.NumberFormat>();
function getDecimalFormatter(digits: number): Intl.NumberFormat {
  let fmt = decimalFormatters.get(digits);
  if (!fmt) {
    fmt = new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    decimalFormatters.set(digits, fmt);
  }
  return fmt;
}

/** Decimal pt-BR genérico — substitui `.toFixed(digits)` inline. */
export function formatDecimal(n: Nullish, digits: number): string {
  if (isNil(n)) return TRAVESSAO;
  return getDecimalFormatter(digits).format(n as number);
}

/** Porcentagem a partir de valor já em % (ex: 8.5 → "8,5%"). Para decimal use formatPercent. */
export function formatPercentRaw(n: Nullish, digits = 1): string {
  if (isNil(n)) return TRAVESSAO;
  return `${getDecimalFormatter(digits).format(n as number)}%`;
}

/** Coordenada lat/long — 6 casas decimais com PONTO decimal (padrão GIS). */
export function formatCoordenada(n: Nullish): string {
  if (isNil(n)) return TRAVESSAO;
  return (n as number).toFixed(6);
}

/** Data ISO (YYYY-MM-DD ou datetime) → DD/MM/YYYY. */
export function formatDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") return TRAVESSAO;
  const datePart = iso.slice(0, 10);
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return TRAVESSAO;
  return `${d}/${m}/${y}`;
}

/** Hora "HH:mm" ou "HH:mm:ss" → "HH:mm". */
export function formatHour(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") return TRAVESSAO;
  // Aceita "08:30", "08:30:00", "2026-04-15T08:30:00" — extrair HH:mm.
  const match = iso.match(/(\d{2}):(\d{2})/);
  if (!match) return TRAVESSAO;
  return `${match[1]}:${match[2]}`;
}

/** Datetime ISO → "DD/MM/YYYY HH:mm" (combina formatDate + formatHour). */
export function formatDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") return TRAVESSAO;
  const date = formatDate(iso);
  if (date === TRAVESSAO) return TRAVESSAO;
  const hour = formatHour(iso);
  return hour === TRAVESSAO ? date : `${date} ${hour}`;
}

/** Ano — 4 dígitos, SEM separador. */
export function formatAno(n: Nullish): string {
  if (isNil(n)) return TRAVESSAO;
  return nfInt.format(Math.round(n as number));
}

/** LOS — letra A-F uppercase. */
export function formatLOS(s: string | null | undefined): string {
  if (s === null || s === undefined || s === "") return TRAVESSAO;
  return s.trim().toUpperCase();
}

// ---------------------------------------------------------------------------
// Parsers (para inputs editáveis)
// ---------------------------------------------------------------------------

/** "8,5%" → 0.085; "3" → 0.03; "3,0" → 0.03. Retorna null se inválido. */
export function parsePercent(input: string): number | null {
  if (input === null || input === undefined) return null;
  const clean = input.trim().replace(/\s/g, "").replace("%", "").replace(",", ".");
  if (clean === "" || clean === "-" || clean === ".") return null;
  const n = Number(clean);
  if (Number.isNaN(n)) return null;
  return n / 100;
}

/** "1,0234" → 1.0234. Retorna null se inválido. */
export function parseFactor(input: string): number | null {
  if (input === null || input === undefined) return null;
  const clean = input.trim().replace(/\s/g, "").replace(",", ".");
  if (clean === "" || clean === "-" || clean === ".") return null;
  const n = Number(clean);
  if (Number.isNaN(n)) return null;
  return n;
}

/** "12.500" → 12500. Aceita "12500", "12.500", "12500,0". Retorna null se inválido. */
export function parseVolume(input: string): number | null {
  if (input === null || input === undefined) return null;
  const clean = input.trim().replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  if (clean === "" || clean === "-") return null;
  const n = Number(clean);
  if (Number.isNaN(n)) return null;
  return Math.round(n);
}

// ---------------------------------------------------------------------------
// Excel cell helper (consumo futuro pelo P-Relatório)
// ---------------------------------------------------------------------------

export type XlsxCellType =
  | "volume"
  | "percent"
  | "factor"
  | "fcp"
  | "velocidade"
  | "distancia"
  | "coordenada"
  | "date"
  | "hour"
  | "ano"
  | "text";

export interface XlsxCell {
  value: number | string | null;
  numFmt?: string;
  type: "number" | "string" | "date" | "null";
}

/** Retorna célula XLSX (value + numFmt + type) para o tipo dado.
 *  Célula ausente vira `{ value: null, type: "null" }` (Excel mostra vazio). */
export function toXlsxCell(
  value: number | string | null | undefined,
  type: XlsxCellType,
): XlsxCell {
  if (value === null || value === undefined || value === "") {
    return { value: null, type: "null" };
  }

  switch (type) {
    case "volume":
      return { value: Math.round(Number(value)), numFmt: "#,##0", type: "number" };
    case "percent":
      // Excel porcentagem: valor permanece decimal, numFmt aplica "%".
      return { value: Number(value), numFmt: "0.0%", type: "number" };
    case "factor":
      return { value: Number(value), numFmt: "0.0000", type: "number" };
    case "fcp":
    case "distancia":
      return { value: Number(value), numFmt: "0.0", type: "number" };
    case "velocidade":
    case "ano":
      return { value: Math.round(Number(value)), numFmt: "0", type: "number" };
    case "coordenada":
      // Coordenada: número com 6 casas (Excel mantém precisão, numFmt controla display).
      return { value: Number(value), numFmt: "0.000000", type: "number" };
    case "date":
      // ISO YYYY-MM-DD: convertemos para string no formato Excel-friendly + numFmt.
      return { value: String(value), numFmt: "dd/mm/yyyy", type: "date" };
    case "hour":
      return { value: String(value), numFmt: "hh:mm", type: "date" };
    case "text":
    default:
      return { value: String(value), type: "string" };
  }
}
